import IconInfo from 'assets/icons/info.svg';
import cn from 'classnames';
import { useConfig } from 'config/use-config';
import { EffectiveCartSubscriptionItem } from 'contexts/cart';
import { useMinSubscriptionValue } from 'hooks/use-min-subscription-value';
import { EffectiveCart } from 'interfaces/cart';
import { PdProductItem } from 'interfaces/line-item';
import { Discount } from 'interfaces/order';
import dynamic from 'next/dynamic';
import { FC, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import CartItem from './cart-item';
import { CURRENCY_2_DECIMALS } from 'constants/formatters';

interface PropTypes {
  canChangeAmount?: boolean;
  canEditSubscription?: boolean;
  discount?: Discount;
  isCartUpdating?: boolean;
  groupedItems?: {
    subscriptionItems: EffectiveCartSubscriptionItem[];
    oneOffPurchaseItems: EffectiveCart['oneOffPurchaseItems'];
  };
  items: EffectiveCart['items'] | PdProductItem[];
  shouldGroupItems?: boolean;
}

const SheetInfoSubscription = dynamic(
  () => import('../../cart/components/cart-items/sheet-info-subscription'),
  { ssr: false }
);

/** Renders funnel items flat or grouped by subscription interval. */
const CartItems: FC<PropTypes> = ({
  canChangeAmount = false,
  canEditSubscription = false,
  discount,
  groupedItems,
  isCartUpdating,
  items,
  shouldGroupItems = false,
}) => {
  const { currencyCode } = useConfig();
  const minimumSubscriptionValue = useMinSubscriptionValue();
  const [selectedSubscription, setSelectedSubscription] = useState<
    EffectiveCartSubscriptionItem | undefined
  >();
  const [isInfoSheetOpen, setIsInfoSheetOpen] = useState(false);
  const { formatNumber } = useIntl();

  if (
    shouldGroupItems &&
    groupedItems?.subscriptionItems &&
    groupedItems?.subscriptionItems.length > 0
  ) {
    return (
      <>
        {groupedItems.subscriptionItems.map((subscription) => {
          const missingAmount = minimumSubscriptionValue - subscription.totalAmount;
          const isSuccess = missingAmount <= 0;
          return (
            <div
              key={subscription.interval}
              data-test={`subscription-funnel-group-${subscription.interval}`}
              className={cn(
                '-mx-1.5 mt-4 mb-4 flex flex-col gap-3 rounded-lg border px-1.5 py-3',
                isSuccess ? 'border-green-400 bg-green-50' : 'border-orange-400 bg-orange-50'
              )}
            >
              <div
                className={cn(
                  'flex items-center justify-between px-0.5 text-sm',
                  isSuccess ? 'text-green-400' : 'text-orange-400'
                )}
              >
                <span data-test={`subscription-funnel-group-status-${subscription.interval}`}>
                  <FormattedMessage
                    id={
                      isSuccess
                        ? 'cart:low-rate-subscription:group:title:success'
                        : 'cart:low-rate-subscription:group:title:missing-amount'
                    }
                    values={{
                      amount: formatNumber(missingAmount / 100, {
                        currency: currencyCode,
                        ...CURRENCY_2_DECIMALS,
                      }),
                    }}
                  />
                </span>
                <button
                  data-test={`subscription-funnel-group-info-button-${subscription.interval}`}
                  onClick={() => {
                    setSelectedSubscription(subscription);
                    setIsInfoSheetOpen(true);
                  }}
                >
                  <IconInfo className="w-h h-5" />
                </button>
              </div>
              {subscription.items.map((item) => (
                <CartItem
                  key={`${subscription.interval}-${item.variantId}`}
                  canChangeAmount={canChangeAmount}
                  canEditSubscription={canEditSubscription}
                  isCartUpdating={isCartUpdating}
                  item={item}
                />
              ))}
            </div>
          );
        })}

        {groupedItems.oneOffPurchaseItems && (
          <div className="flex flex-col gap-2.5">
            {groupedItems.oneOffPurchaseItems.map((item) => (
              <CartItem
                key={`one-off-${item.variantId}`}
                canChangeAmount={canChangeAmount}
                canEditSubscription={canEditSubscription}
                isCartUpdating={isCartUpdating}
                item={item}
              />
            ))}
          </div>
        )}

        <SheetInfoSubscription
          discount={discount}
          isOpen={isInfoSheetOpen}
          subscriptionData={selectedSubscription}
          onClose={() => setIsInfoSheetOpen(false)}
        />
      </>
    );
  }

  return (
    <div className="mt-4 flex flex-col gap-3">
      {items.map((item) => (
        <CartItem
          key={`one-off-${item.variantId}`}
          canChangeAmount={canChangeAmount}
          canEditSubscription={canEditSubscription}
          isCartUpdating={isCartUpdating}
          item={item}
        />
      ))}
    </div>
  );
};

export default CartItems;
